import { round2 } from "../../utils/money";
// frontend/src/components/SalesOrdersList.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  Package, Plus, Search, Eye, Edit, Trash2,
  Loader2, CheckCircle, XCircle, Clock, AlertCircle, ChevronDown, ChevronUp,
  ArrowRight
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
// import "./SalesOrdersList.css";

const url = process.env.REACT_APP_BACKEND_URL;

const SalesOrdersList = () => {
  const { currentUser, userType, company } = useAuth();
  const { formatCurrency, getCurrencySymbol } = useCurrency();
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [filters, setFilters] = useState({
    status: "",
    customer_id: "",
    start_date: "",
    end_date: "",
    search: ""
  });

  const getCompanyId = () => {
    if (userType === 'company' && company) {
      return company._id || company;
    }
    return null;
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const companyId = getCompanyId();

      const params = new URLSearchParams({
        company_id: companyId,
        ...filters
      });

      const response = await axios.get(`${url}/api/salesorders?${params}`);

      if (response.data.success) {
        setOrders(response.data.data);
      }
    } catch (error) {
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleSearch = () => {
    fetchOrders();
  };

  const handleView = (id) => {
    navigate(`/salesorders/${id}`);
  };

  const handleEdit = (id) => {
    navigate(`/salesorders/edit/${id}`);
  };

  const handleConvertToInvoice = (id) => {
    navigate(`/salesorders/${id}/convert`);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to cancel this order?")) return;

    try {
      await axios.delete(`${url}/api/salesorders/${id}`);
      toast.success("Order cancelled successfully");
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to cancel order");
    }
  };

  const toggleExpand = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { color: '#fa8c16', bg: '#fff7e6', icon: <Clock size={14} /> },
      confirmed: { color: '#1890ff', bg: '#e6f7ff', icon: <CheckCircle size={14} /> },
      processing: { color: '#722ed1', bg: '#f9f0ff', icon: <Loader2 size={14} className="animate-spin" /> },
      shipped: { color: '#13c2c2', bg: '#e6fffb', icon: <Package size={14} /> },
      delivered: { color: '#52c41a', bg: '#f6ffed', icon: <CheckCircle size={14} /> },
      cancelled: { color: '#ff4d4f', bg: '#fff2f0', icon: <XCircle size={14} /> },
      partially_delivered: { color: '#fa8c16', bg: '#fff7e6', icon: <AlertCircle size={14} /> }
    };
    const badge = badges[status] || badges.draft;

    return (
      <span style={{
        background: badge.bg,
        color: badge.color,
        padding: '4px 8px',
        borderRadius: 12,
        fontSize: 12,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4
      }}>
        {badge.icon} {status.replace('_', ' ')}
      </span>
    );
  };


  const getOrderDisplayTotals = (order) => {
    const items = Array.isArray(order?.items) ? order.items : [];

    const calculatedSubtotal = items.reduce(
      (sum, item) =>
        sum +
        (Number(item.quantity) || 0) *
        (Number(item.unit_price) || 0),
      0,
    );

    const subtotal = Number(
      order?.subtotal ??
      order?.sub_total ??
      order?.total_before_discount ??
      calculatedSubtotal,
    );

    const selectedDiscount = Number(
      order?.selected_discount_amount ??
      order?.discount_amount ??
      0,
    );

    const manualDiscount = Number(order?.manual_discount || 0);

    const totalDiscount = Number(
      order?.total_discount ??
      (selectedDiscount + manualDiscount),
    );

    const selectedTax = Number(
      order?.selected_tax_amount ??
      order?.tax_amount ??
      0,
    );

    const manualTax = Number(order?.manual_tax || 0);

    const totalTax = Number(
      order?.total_tax ??
      (selectedTax + manualTax),
    );

    const shipping = Number(order?.shipping_cost || 0);
    const other = Number(order?.other_charges || 0);

    const explicitGrandTotal = Number(
      order?.grand_total ??
      order?.total_amount ??
      order?.net_amount,
    );

    const calculatedGrandTotal =
      subtotal - totalDiscount + totalTax + shipping + other;

    return {
      subtotal,
      totalDiscount,
      totalTax,
      shipping,
      other,
      grandTotal:
        Number.isFinite(explicitGrandTotal) && explicitGrandTotal > 0
          ? explicitGrandTotal
          : calculatedGrandTotal,
    };
  };

  const hasSavedAdjustments = (order) =>
    Number(order?.total_discount || 0) > 0 ||
    Number(order?.total_tax || 0) > 0 ||
    Number(order?.manual_discount || 0) > 0 ||
    Number(order?.manual_tax || 0) > 0 ||
    Boolean(order?.discount_id) ||
    Boolean(order?.tax_id) ||
    (Array.isArray(order?.items) &&
      order.items.some(
        (item) =>
          Number(item?.discount_amount || 0) > 0 ||
          Number(item?.discount_flat || 0) > 0 ||
          Number(item?.tax_amount || 0) > 0,
      ));

  const getItemDisplayAmounts = (order, item, itemIndex = 0) => {
    const totals = getOrderDisplayTotals(order);
    const items = Array.isArray(order?.items) ? order.items : [];

    const gross =
      (Number(item?.quantity) || 0) *
      (Number(item?.unit_price) || 0);

    const grossValues = items.map(
      (row) =>
        (Number(row?.quantity) || 0) *
        (Number(row?.unit_price) || 0),
    );

    const subtotal =
      totals.subtotal > 0
        ? totals.subtotal
        : grossValues.reduce((sum, value) => sum + value, 0);

    const ratio = subtotal > 0 ? gross / subtotal : 0;
    const isLastItem = itemIndex === items.length - 1;

    const previousGross = grossValues
      .slice(0, itemIndex)
      .reduce((sum, value) => sum + value, 0);

    const previousRatio =
      subtotal > 0 ? previousGross / subtotal : 0;

    const discountAmount = isLastItem
      ? totals.totalDiscount -
      Number(round2(totals.totalDiscount * previousRatio))
      : Number(round2(totals.totalDiscount * ratio));

    const taxAmount = isLastItem
      ? totals.totalTax -
      Number(round2(totals.totalTax * previousRatio))
      : Number(round2(totals.totalTax * ratio));

    const shippingAmount = isLastItem
      ? totals.shipping -
      Number(round2(totals.shipping * previousRatio))
      : Number(round2(totals.shipping * ratio));

    const otherAmount = isLastItem
      ? totals.other -
      Number(round2(totals.other * previousRatio))
      : Number(round2(totals.other * ratio));

    const total =
      gross -
      discountAmount +
      taxAmount +
      shippingAmount +
      otherAmount;

    return {
      gross,
      ratio,
      discountAmount: Number(round2(discountAmount)),
      taxAmount: Number(round2(taxAmount)),
      shippingAmount: Number(round2(shippingAmount)),
      otherAmount: Number(round2(otherAmount)),
      total: Number(round2(total)),
    };
  };

  const getDiscountDescription = (order) => {
    const selectedName =
      order?.discount_id?.name ||
      order?.discount_name ||
      "";

    const selectedAmount = Number(
      order?.selected_discount_amount || 0,
    );

    const manualAmount = Number(order?.manual_discount || 0);

    if (selectedName && selectedAmount > 0 && manualAmount > 0) {
      return `${selectedName} + manual adjustment`;
    }

    if (selectedName) return selectedName;
    if (manualAmount > 0) return "Manual discount";

    return "Order discount";
  };

  const getTaxDescription = (order) => {
    const rawName =
      order?.tax_id?.name ||
      order?.tax_name ||
      "Order tax";

    const rate = Number(
      order?.tax_value ||
      order?.tax_id?.value ||
      0,
    );

    const nameAlreadyHasRate = /(?:^|\s)\d+(?:\.\d+)?%/.test(rawName);

    return rate > 0 && !nameAlreadyHasRate
      ? `${rawName} ${rate}%`
      : rawName;
  };

  const renderAdjustmentCell = ({
    amount,
    description,
    prefix,
    color,
  }) => {
    if (!(Number(amount) > 0)) return "-";

    return (
      <div>
        <div style={{ fontWeight: 600, color }}>
          {prefix}
          {formatCurrency(amount)}
        </div>
        <div
          style={{
            marginTop: 2,
            fontSize: 10,
            color: "#777",
            lineHeight: 1.25,
          }}
        >
          {description}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Loader2 className="animate-spin" size={32} />
        <p>Loading orders...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 20 }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20
      }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Package size={24} /> Sales Orders
        </h2>
        <button
          onClick={() => navigate("/AddSalesOrder")}
          style={{
            padding: '10px 20px',
            background: '#1890ff',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          <Plus size={16} /> New Order
        </button>
      </div>

      {/* Filters */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 10,
        marginBottom: 20,
        padding: 20,
        background: '#f5f5f5',
        borderRadius: 4
      }}>
        <select
          name="status"
          value={filters.status}
          onChange={handleFilterChange}
          style={{ padding: 8 }}
        >
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="confirmed">Confirmed</option>
          <option value="processing">Processing</option>
          <option value="shipped">Shipped</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
          <option value="partially_delivered">Partially Delivered</option>
        </select>

        <input
          type="date"
          name="start_date"
          value={filters.start_date}
          onChange={handleFilterChange}
          placeholder="Start Date"
          style={{ padding: 8 }}
        />

        <input
          type="date"
          name="end_date"
          value={filters.end_date}
          onChange={handleFilterChange}
          placeholder="End Date"
          style={{ padding: 8 }}
        />

        <div style={{ display: 'flex', gap: 10 }}>
          <input
            type="text"
            name="search"
            value={filters.search}
            onChange={handleFilterChange}
            placeholder="Search reference..."
            style={{ flex: 1, padding: 8 }}
          />
          <button
            onClick={handleSearch}
            style={{
              padding: '8px 16px',
              background: '#1890ff',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer'
            }}
          >
            <Search size={16} />
          </button>
        </div>
      </div>

      {/* Orders Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f5f5f5' }}>
              <th style={thStyle}></th>
              <th style={thStyle}>Reference</th>
              <th style={thStyle}>Order No</th>
              <th style={thStyle}>Customer</th>
              <th style={thStyle}>Date</th>
              <th style={thStyle}>Items</th>
              <th style={thStyle}>Total</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Payment</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.map(order => {
              const displayTotals = getOrderDisplayTotals(order);

              return (
                <React.Fragment key={order._id}>
                  <tr style={{ borderBottom: '1px solid #eee', background: expandedOrder === order._id ? '#f9f9f9' : 'white' }}>
                    <td style={tdStyle}>
                      <button
                        onClick={() => toggleExpand(order._id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {expandedOrder === order._id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                    </td>
                    <td style={tdStyle}>
                      <strong>{order.reference_id}</strong>
                    </td>
                    <td style={tdStyle}>{order.order_no}</td>
                    <td style={tdStyle}>
                      {order.customer_id?.customer_name || 'N/A'}
                      {order.customer_id?.phone && (
                        <div style={{ fontSize: 11, color: '#666' }}>{order.customer_id.phone}</div>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {new Date(order.order_date).toLocaleDateString()}
                      {order.expected_delivery_date && (
                        <div style={{ fontSize: 11, color: '#666' }}>
                          Expected: {new Date(order.expected_delivery_date).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600 }}>{order.items.length} items</div>
                      <div style={{ fontSize: 11, color: '#52c41a' }}>
                        Delivered: {order.items.filter(i => i.status === 'delivered').length}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <strong style={{ color: "#1890ff" }}>
                        {formatCurrency(displayTotals.subtotal)}
                      </strong>
                      <div
                        style={{
                          fontSize: 11,
                          color: "#666",
                          marginTop: 2,
                        }}
                      >
                        Net:{" "}
                        <strong>
                          {formatCurrency(displayTotals.grandTotal)}
                        </strong>
                      </div>
                      {displayTotals.totalDiscount > 0 && (
                        <div style={{ fontSize: 11, color: "#666" }}>
                          Discount: {formatCurrency(displayTotals.totalDiscount)}
                        </div>
                      )}
                      {displayTotals.totalTax > 0 && (
                        <div style={{ fontSize: 11, color: "#666" }}>
                          Tax: {formatCurrency(displayTotals.totalTax)}
                        </div>
                      )}
                      {!hasSavedAdjustments(order) && (
                        <div style={{ fontSize: 10, color: '#fa8c16' }}>
                          Legacy order: discount/tax was not saved
                        </div>
                      )}
                    </td>
                    <td style={tdStyle}>{getStatusBadge(order.status)}</td>
                    <td style={tdStyle}>
                      <span style={{
                        color: order.payment_status === 'paid' ? '#52c41a' :
                          order.payment_status === 'partial' ? '#fa8c16' : '#ff4d4f'
                      }}>
                        {order.payment_status}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 5 }}>
                        <button
                          onClick={() => handleView(order._id)}
                          style={actionBtn}
                          title="View"
                        >
                          <Eye size={16} />
                        </button>
                        {order.status !== 'delivered' && order.status !== 'cancelled' && order.status !== 'partially_delivered' && (
                          <>
                            <button
                              onClick={() => handleEdit(order._id)}
                              style={{ ...actionBtn, background: '#1890ff', color: 'white' }}
                              title="Edit"
                            >
                              <Edit size={16} />
                            </button>
                            {(order.status === 'confirmed' || order.status === 'processing' || order.status === 'draft') && (
                              <button
                                onClick={() => handleConvertToInvoice(order._id)}
                                style={{ ...actionBtn, background: '#52c41a', color: 'white' }}
                                title="Convert to Invoice"
                              >
                                <ArrowRight size={16} />
                              </button>
                            )}
                           
                          </>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Expanded Items Row */}
                  {expandedOrder === order._id && (
                    <tr>
                      <td colSpan="10" style={{ padding: 0, background: '#f9f9f9' }}>
                        <div style={{ padding: '20px' }}>
                          <h4 style={{ margin: '0 0 15px 0', color: '#333' }}>Order Items</h4>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ background: '#e9e9e9' }}>
                                <th style={itemThStyle}>Product</th>
                                <th style={itemThStyle}>Quantity</th>
                                <th style={itemThStyle}>Unit Price</th>
                                <th style={itemThStyle}>Discount Allocation</th>
                                <th style={itemThStyle}>Tax Allocation</th>
                                <th style={itemThStyle}>Total</th>
                                <th style={itemThStyle}>Delivered</th>
                                <th style={itemThStyle}>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {order.items.map((item, idx) => (
                                <tr key={idx} style={{ borderBottom: '1px solid #ddd' }}>
                                  <td style={itemTdStyle}>
                                    <div>
                                      <strong>{item.product_id?.product_name || 'Unknown Product'}</strong>
                                    </div>
                                    <div style={{ fontSize: 11, color: '#666' }}>
                                      Code: {item.product_id?.product_code || 'N/A'}
                                    </div>
                                    {item.notes && (
                                      <div style={{ fontSize: 11, color: '#666', fontStyle: 'italic' }}>
                                        Note: {item.notes}
                                      </div>
                                    )}
                                  </td>
                                  <td style={itemTdStyle}>
                                    <strong>{item.quantity}</strong>
                                  </td>
                                  <td style={itemTdStyle}>
                                    {formatCurrency(item.unit_price)}
                                  </td>
                                  <td style={itemTdStyle}>
                                    {renderAdjustmentCell({
                                      amount:
                                        getItemDisplayAmounts(order, item, idx)
                                          .discountAmount,
                                      description:
                                        getDiscountDescription(order),
                                      prefix: "",
                                      color: "#333",
                                    })}
                                  </td>
                                  <td style={itemTdStyle}>
                                    {renderAdjustmentCell({
                                      amount:
                                        getItemDisplayAmounts(order, item, idx)
                                          .taxAmount,
                                      description:
                                        getTaxDescription(order),
                                      prefix: "",
                                      color: "#595959",
                                    })}
                                  </td>
                                  <td style={itemTdStyle}>
                                    <strong style={{ color: '#1890ff' }}>
                                      {formatCurrency(
                                        getItemDisplayAmounts(order, item, idx).total,
                                      )}
                                    </strong>
                                  </td>
                                  <td style={itemTdStyle}>
                                    <div>
                                      <span style={{
                                        color: item.delivered_quantity >= item.quantity ? '#52c41a' : '#fa8c16',
                                        fontWeight: 600
                                      }}>
                                        {item.delivered_quantity || 0} / {item.quantity}
                                      </span>
                                    </div>
                                  </td>
                                  <td style={itemTdStyle}>
                                    <span style={{
                                      padding: '4px 8px',
                                      borderRadius: 12,
                                      fontSize: 11,
                                      background: item.status === 'delivered' ? '#f6ffed' :
                                        item.status === 'partially_delivered' ? '#fff7e6' : '#e6f7ff',
                                      color: item.status === 'delivered' ? '#52c41a' :
                                        item.status === 'partially_delivered' ? '#fa8c16' : '#1890ff'
                                    }}>
                                      {item.status?.replace('_', ' ') || 'pending'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {orders.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: 60,
          background: '#fafafa',
          borderRadius: 8,
          marginTop: 20
        }}>
          <Package size={48} color="#ccc" />
          <h3 style={{ margin: '20px 0 10px', color: '#666' }}>No Orders Found</h3>
          <p style={{ color: '#999' }}>Create your first sales order to get started</p>
          <button
            onClick={() => navigate("/AddSalesOrder")}
            style={{
              marginTop: 20,
              padding: '10px 20px',
              background: '#1890ff',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <Plus size={16} /> Create Order
          </button>
        </div>
      )}
    </div>
  );
};

const thStyle = {
  padding: '12px',
  textAlign: 'left',
  fontWeight: 600,
  borderBottom: '2px solid #ddd'
};

const tdStyle = {
  padding: '12px',
  verticalAlign: 'middle'
};

const itemThStyle = {
  padding: '10px',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '12px',
  borderBottom: '1px solid #ccc'
};

const itemTdStyle = {
  padding: '10px',
  verticalAlign: 'top',
  fontSize: '13px'
};

const actionBtn = {
  padding: '5px 10px',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#f0f0f0',
  color: '#666'
};

export default SalesOrdersList;
